/**
 * Helper functions for NPRM document processing
 */

import { getFilingDetails } from '../lib/ecfsBrowserClient';

export interface NPRMDocument {
  filename: string;
  src?: string;
  url?: string;
  file_url?: string;
  document_url?: string;
  link?: string;
  href?: string;
  type?: string;
}

export interface ExtractedDeadline {
  date: Date;
  text: string;
  context: string;
  isReplyComment?: boolean; // Flag to indicate if this is a reply comment deadline
}

export interface CalculatedDeadlines {
  publicationDate: Date;
  commentDeadline: Date;
  replyCommentDeadline: Date;
}

export type DeadlineType = 'comment' | 'reply_comment' | 'publication' | 'other';

export interface TimelinePeriod {
  start: Date;
  end: Date | null;
  label: string;
  status: 'active' | 'upcoming' | 'past';
}

export interface TimelineData {
  fccReleaseDate: Date;
  federalRegisterPublicationDate: Date | null;
  commentDeadline: Date | null;
  replyCommentDeadline: Date | null;
  commentsPeriod: TimelinePeriod | null;
  replyCommentsPeriod: TimelinePeriod | null;
}

/**
 * Find the A1 document from a list of documents
 * Priority: Look for files ending in _A1.pdf or A1.pdf (case-insensitive)
 * Fallback: If no A1 file, return first PDF document
 */
export function findA1Document(documents: NPRMDocument[]): NPRMDocument | null {
  if (!documents || documents.length === 0) {
    return null;
  }

  // First, look for A1 document (case-insensitive)
  const a1Document = documents.find((doc) => {
    const filename = (doc.filename || '').toLowerCase();
    return filename.endsWith('_a1.pdf') || filename.endsWith('a1.pdf');
  });

  if (a1Document) {
    return a1Document;
  }

  // Fallback: return first PDF document
  const pdfDocument = documents.find((doc) => {
    const filename = (doc.filename || '').toLowerCase();
    return filename.endsWith('.pdf') || (doc.type && doc.type.includes('pdf'));
  });

  return pdfDocument || documents[0] || null;
}

/**
 * Extract deadlines from document text
 * Looks for common deadline patterns in NPRM documents
 */
// Helper function to parse date string as UTC (to avoid timezone issues)
function parseDateAsUTC(dateStr: string): Date | null {
  try {
    // Try parsing as "Month DD, YYYY" format
    const monthNameMatch = dateStr.match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})/i);
    if (monthNameMatch) {
      const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                         'july', 'august', 'september', 'october', 'november', 'december'];
      const month = monthNames.indexOf(monthNameMatch[1].toLowerCase());
      const day = parseInt(monthNameMatch[2], 10);
      const year = parseInt(monthNameMatch[3], 10);
      // Create date in UTC
      return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
    }
    
    // Try parsing as "MM/DD/YYYY" format
    const slashMatch = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slashMatch) {
      const month = parseInt(slashMatch[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(slashMatch[2], 10);
      const year = parseInt(slashMatch[3], 10);
      // Create date in UTC
      return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
    }
    
    // Fallback to standard Date parsing
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      // If it's a valid date, convert to UTC by using UTC methods
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
    }
    
    return null;
  } catch (err) {
    return null;
  }
}

export function extractDeadlinesFromText(text: string): ExtractedDeadline[] {
  if (!text) {
    return [];
  }

  const deadlines: ExtractedDeadline[] = [];
  const deadlineKeywords = [
    'deadline',
    'comments are due',
    'comments due',
    'comments will be accepted',
    'comments will be accepted until',
    'accepted until',
    'filing deadline',
    'reply comments are due',
    'reply comments due',
    'reply comments will be accepted',
    'reply comments will be accepted until',
    'must be received',
    'must be filed',
    'submission deadline',
    'comment period ends',
    'comment period closes',
  ];

  // Date patterns: MM/DD/YYYY, M/D/YYYY, Month DD, YYYY, etc.
  const datePatterns = [
    // MM/DD/YYYY or M/D/YYYY
    /\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g,
    // Month DD, YYYY
    /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})\b/gi,
    // Month DD YYYY (without comma)
    /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2})\s+(\d{4})\b/gi,
    // DD Month YYYY
    /\b(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})\b/gi,
  ];

  // First, try to find dates directly after deadline phrases (more precise)
  // IMPORTANT: Check for "Reply comments" FIRST before "Comments" to avoid false matches
  // We'll process them separately to mark which are reply comments
  const replyCommentPatterns = [
    // "Reply comments will be accepted until [DATE]"
    /reply\s+comments\s+will\s+be\s+accepted\s+until\s+((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4})/gi,
    // "Reply comments are accepted until [DATE]"
    /reply\s+comments\s+are\s+accepted\s+until\s+((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4})/gi,
    // "Reply comments are due [DATE]" / "Reply comments due [DATE]" / "Reply comments due: [DATE]" (Bureau PNs)
    /reply\s+comments\s+(?:are\s+)?due\s*[:\s]\s*(?:on\s+or\s+before\s+)?((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4})/gi,
    // Bureau PN format — "Reply Comment Date: May 18, 2026" / "Reply Comment Deadline: ..."
    // This is the dominant format on DA-XX-XXX-style Bureau Public Notices.
    /reply\s+comments?\s+(?:date|deadline)s?\s*:\s*((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4})/gi,
  ];

  const commentPatterns = [
    // "Comments will be accepted until [DATE]"
    /comments\s+will\s+be\s+accepted\s+until\s+((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4})/gi,
    // "Comments are due [DATE]" / "Comments due [DATE]" / "Comments due: [DATE]" (Bureau PNs)
    /comments\s+(?:are\s+)?due\s*[:\s]\s*(?:on\s+or\s+before\s+)?((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4})/gi,
    // Bureau PN format — "Comment Date: May 1, 2026" / "Comment Deadline: ..."
    // The reply-pattern runs first and the beforeMatch.includes('reply')
    // check below filters out the May-18 instance that lives inside
    // "Reply Comment Date: ...", so we don't double-count.
    /comments?\s+(?:date|deadline)s?\s*:\s*((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{4})/gi,
  ];
  
  // Process reply comment patterns first
  replyCommentPatterns.forEach((pattern) => {
    const matches = [...text.matchAll(pattern)];
    for (const match of matches) {
      try {
        const dateStr = match[1];
        const fullMatch = match[0];
        const date = parseDateAsUTC(dateStr);
        
        if (date && !isNaN(date.getTime())) {
          const now = new Date();
          const fiveYearsAgo = new Date(now.getFullYear() - 5, 0, 1);
          const fiveYearsFromNow = new Date(now.getFullYear() + 5, 11, 31);

          if (date >= fiveYearsAgo && date <= fiveYearsFromNow) {
            deadlines.push({
              date,
              text: dateStr,
              context: fullMatch.trim(),
              isReplyComment: true, // Mark as reply comment
            });
          }
        }
      } catch (err) {
        console.warn('Failed to parse date from reply comment pattern:', match[1], err);
      }
    }
  });
  
  // Process regular comment patterns (but skip if already found as reply comment)
  const foundReplyDates = new Set(deadlines.filter(d => d.isReplyComment).map(d => d.date.getTime()));
  
  commentPatterns.forEach((pattern) => {
    const matches = [...text.matchAll(pattern)];
    for (const match of matches) {
      try {
        const dateStr = match[1];
        const fullMatch = match[0];
        const date = parseDateAsUTC(dateStr);
        
        if (date && !isNaN(date.getTime())) {
          const now = new Date();
          const fiveYearsAgo = new Date(now.getFullYear() - 5, 0, 1);
          const fiveYearsFromNow = new Date(now.getFullYear() + 5, 11, 31);

          if (date >= fiveYearsAgo && date <= fiveYearsFromNow) {
            // Skip if this date was already found as a reply comment
            if (!foundReplyDates.has(date.getTime())) {
              // Also check that the match doesn't have "reply" before it
              const matchIndex = text.toLowerCase().indexOf(fullMatch.toLowerCase());
              if (matchIndex >= 0) {
                const beforeMatch = text.substring(Math.max(0, matchIndex - 50), matchIndex).toLowerCase();
                if (!beforeMatch.includes('reply')) {
                  deadlines.push({
                    date,
                    text: dateStr,
                    context: fullMatch.trim(),
                    isReplyComment: false,
                  });
                }
              }
            }
          }
        }
      } catch (err) {
        console.warn('Failed to parse date from comment pattern:', match[1], err);
      }
    }
  });
  

  // Fallback: Split text into sentences for better context (if direct patterns didn't find anything)
  if (deadlines.length === 0) {
    const sentences = text.split(/[.!?]\s+/);

    sentences.forEach((sentence) => {
      const lowerSentence = sentence.toLowerCase();

      // Check if sentence contains deadline keywords
      const hasDeadlineKeyword = deadlineKeywords.some((keyword) =>
        lowerSentence.includes(keyword.toLowerCase())
      );

      if (hasDeadlineKeyword) {
        // Try to find dates in this sentence
        for (const pattern of datePatterns) {
          const matches = [...sentence.matchAll(pattern)];
          for (const match of matches) {
            try {
              let date: Date | null = null;

            if (pattern.source.includes('\\d{1,2}')) {
              // MM/DD/YYYY format
              const month = parseInt(match[1], 10);
              const day = parseInt(match[2], 10);
              const year = parseInt(match[3], 10);
              // Create date in UTC
              date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
            } else {
              // Month name format
              const dateStr = match[0];
              date = parseDateAsUTC(dateStr);
            }

              if (date && !isNaN(date.getTime())) {
                // Validate date is reasonable (not too far in past or future)
                const now = new Date();
                const fiveYearsAgo = new Date(now.getFullYear() - 5, 0, 1);
                const fiveYearsFromNow = new Date(now.getFullYear() + 5, 11, 31);

                if (date >= fiveYearsAgo && date <= fiveYearsFromNow) {
                  deadlines.push({
                    date,
                    text: match[0],
                    context: sentence.trim(),
                  });
                }
              }
            } catch (err) {
              // Skip invalid dates
              console.warn('Failed to parse date:', match[0], err);
            }
          }
        }
      }
    });
  }

  // Remove duplicates and sort by date
  const uniqueDeadlines = deadlines.filter(
    (deadline, index, self) =>
      index ===
      self.findIndex(
        (d) =>
          d.date.getTime() === deadline.date.getTime() &&
          d.text === deadline.text
      )
  );

  return uniqueDeadlines.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Plain text from Federal Register HTML (main document body, not chrome).
 * Browser-only (uses DOMParser).
 */
export function getFederalRegisterMainText(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const mainContent =
    doc.querySelector(
      '.document-content, .document-full-text, main, article, [role="main"]'
    ) ||
    doc.querySelector('#document-content, #document-full-text') ||
    doc.body;
  return mainContent.textContent || doc.body.textContent || '';
}

/** Normalize a DB / ISO deadline to UTC calendar midnight (matches timeline display). */
export function parseTimelineDeadlineDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
}

/**
 * A cached `nprm_timelines` row is stale when its Federal Register publication date
 * precedes the FCC release date. FR always publishes an NPRM on or after its FCC
 * release, so an FR pub date earlier than the FCC release means the row's FR-derived
 * fields (publication date, comment/reply deadlines, NPRM name) were matched against
 * an older NPRM cycle and should be ignored / repopulated.
 */
export function isTimelineRowStale(timeline: {
  fcc_release_date?: string | null;
  federal_register_publication_date?: string | null;
}): boolean {
  if (!timeline?.fcc_release_date || !timeline?.federal_register_publication_date) {
    return false;
  }
  const fcc = new Date(timeline.fcc_release_date).getTime();
  const fr = new Date(timeline.federal_register_publication_date).getTime();
  if (Number.isNaN(fcc) || Number.isNaN(fr)) return false;
  return fr < fcc;
}

/** Short regulatory calendar label in UTC (same calendar day as NPRM timeline tags). */
export function formatDeadlineCalendarDate(isoOrDate: string | Date | null | undefined): string {
  if (isoOrDate === null || isoOrDate === undefined) return 'N/A';
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export interface ClassifiedDeadlines {
  foundCommentDeadline: Date | null;
  foundReplyCommentDeadline: Date | null;
}

/**
 * Map extracted FR deadline hits to comment vs reply (same rules as NPRM timeline enrichment).
 */
export function classifyDeadlinesFromExtracted(
  extracted: ExtractedDeadline[],
  textContent: string
): ClassifiedDeadlines {
  let foundCommentDeadline: Date | null = null;
  let foundReplyCommentDeadline: Date | null = null;

  if (extracted.length === 0) {
    return { foundCommentDeadline, foundReplyCommentDeadline };
  }

  const lowerFull = textContent.toLowerCase();

  for (const deadline of extracted) {
    const context = deadline.context.toLowerCase();

    if (deadline.isReplyComment === true) {
      if (!foundReplyCommentDeadline || deadline.date > foundReplyCommentDeadline) {
        foundReplyCommentDeadline = deadline.date;
      }
      continue;
    }

    // Direct comment-pattern hits (set by extractDeadlinesFromText). The
    // extractor only sets isReplyComment=false when the comment-pattern
    // regex matched, so this is reliable — trust it before falling
    // through to the context-string heuristics, which would otherwise
    // miss Bureau PN formats like "Comment Date: ...".
    if (deadline.isReplyComment === false) {
      if (!foundCommentDeadline || deadline.date < foundCommentDeadline) {
        foundCommentDeadline = deadline.date;
      }
      continue;
    }

    if (
      context.includes('reply comments will be accepted until') ||
      context.includes('reply comments are accepted until') ||
      context.includes('reply comments will be accepted')
    ) {
      if (!foundReplyCommentDeadline || deadline.date > foundReplyCommentDeadline) {
        foundReplyCommentDeadline = deadline.date;
      }
      continue;
    }

    if (
      (context.includes('comments will be accepted until') ||
        context.includes('comments are accepted until')) &&
      !context.includes('reply')
    ) {
      if (!foundCommentDeadline || deadline.date < foundCommentDeadline) {
        foundCommentDeadline = deadline.date;
      }
      continue;
    }

    const deadlineIndex = lowerFull.indexOf(context);
    if (deadlineIndex >= 0) {
      const contextWindow = textContent
        .substring(
          Math.max(0, deadlineIndex - 200),
          Math.min(textContent.length, deadlineIndex + deadline.context.length + 200)
        )
        .toLowerCase();

      const hasReplyComments =
        contextWindow.includes('reply comments') || contextWindow.includes('reply comment');

      if (
        hasReplyComments &&
        contextWindow.includes('comments will be accepted') &&
        context.includes('comments will be accepted until')
      ) {
        if (!foundReplyCommentDeadline || deadline.date > foundReplyCommentDeadline) {
          foundReplyCommentDeadline = deadline.date;
        }
        continue;
      }
    }
  }

  if (!foundCommentDeadline || !foundReplyCommentDeadline) {
    for (const deadline of extracted) {
      const context = deadline.context.toLowerCase();

      if (
        !foundReplyCommentDeadline &&
        (context.includes('reply comments') || context.includes('reply comment'))
      ) {
        if (!foundReplyCommentDeadline || deadline.date > foundReplyCommentDeadline) {
          foundReplyCommentDeadline = deadline.date;
        }
      } else if (
        !foundCommentDeadline &&
        (context.includes('comments are due') ||
          context.includes('comments due') ||
          context.includes('comment deadline') ||
          context.includes('comment period')) &&
        !context.includes('reply')
      ) {
        if (!foundCommentDeadline || deadline.date < foundCommentDeadline) {
          foundCommentDeadline = deadline.date;
        }
      }
    }
  }

  if (!foundCommentDeadline && !foundReplyCommentDeadline && extracted.length > 0) {
    const sortedDeadlines = [...extracted].sort((a, b) => a.date.getTime() - b.date.getTime());
    if (sortedDeadlines.length >= 1) {
      foundCommentDeadline = sortedDeadlines[0].date;
    }
    if (sortedDeadlines.length >= 2) {
      foundReplyCommentDeadline = sortedDeadlines[1].date;
    }
  }

  return { foundCommentDeadline, foundReplyCommentDeadline };
}

/**
 * Calculate deadlines from Federal Register publication date
 * Comments are due 30 days after publication, reply comments 60 days after
 */
export function calculateDeadlinesFromPublicationDate(
  publicationDate: Date
): CalculatedDeadlines {
  const commentDeadline = new Date(publicationDate);
  commentDeadline.setDate(commentDeadline.getDate() + 30);

  const replyCommentDeadline = new Date(publicationDate);
  replyCommentDeadline.setDate(replyCommentDeadline.getDate() + 60);

  return {
    publicationDate,
    commentDeadline,
    replyCommentDeadline,
  };
}

/**
 * Format deadline date with relative time
 */
export function formatDeadlineDate(
  date: Date,
  type?: DeadlineType
): {
  formatted: string;
  relative: string;
  isPastDue: boolean;
  isApproaching: boolean;
  label: string;
} {
  const now = new Date();
  const diffTime = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Format date in UTC to avoid timezone issues
  const formatted = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });

  let relative = '';
  let isPastDue = false;
  let isApproaching = false;

  if (diffDays < 0) {
    const daysPast = Math.abs(diffDays);
    relative = `${daysPast} day${daysPast === 1 ? '' : 's'} ago`;
    isPastDue = true;
  } else if (diffDays === 0) {
    relative = 'Today';
    isApproaching = true;
  } else if (diffDays === 1) {
    relative = 'Tomorrow';
    isApproaching = true;
  } else if (diffDays <= 7) {
    relative = `in ${diffDays} days`;
    isApproaching = true;
  } else if (diffDays <= 30) {
    relative = `in ${diffDays} days`;
  } else {
    const weeks = Math.floor(diffDays / 7);
    relative = `in ${weeks} week${weeks === 1 ? '' : 's'}`;
  }

  // Generate label based on deadline type
  let label = 'Deadline';
  if (type === 'comment') {
    label = 'Comments Due';
  } else if (type === 'reply_comment') {
    label = 'Reply Comments Due';
  } else if (type === 'publication') {
    label = 'Published in Federal Register';
  }

  return {
    formatted,
    relative,
    isPastDue,
    isApproaching,
    label,
  };
}

/**
 * Get document URL from Supabase storage or ECFS API
 */
export async function getDocumentUrl(
  docketNumber: string,
  document: NPRMDocument,
  submissionId?: string
): Promise<string | null> {
  // First, try to get URL from document object
  const docUrl =
    document.src ||
    document.url ||
    document.file_url ||
    document.document_url ||
    document.link ||
    document.href;

  if (docUrl) {
    return docUrl;
  }

  // If we have submissionId, fetch directly from ecfsapi.fcc.gov (browser-direct,
  // bypasses the Supabase edge function because Akamai blocks datacenter egress).
  if (submissionId) {
    try {
      const data = await getFilingDetails(submissionId);
      if (data) {
        const documents = (data as any).documents || [];
        const matchingDoc = documents.find(
          (doc: any) => doc.filename === document.filename
        );
        if (matchingDoc) {
          return (
            matchingDoc.src ||
            matchingDoc.url ||
            matchingDoc.file_url ||
            matchingDoc.document_url ||
            null
          );
        }
      }
    } catch (err) {
      console.warn('Failed to fetch document URL from ECFS API:', err);
    }
  }

  return null;
}

/**
 * Calculate time windows from timeline dates
 * Returns Comments period and Reply Comments period
 */
export function calculateTimeWindows(
  fccReleaseDate: Date,
  federalRegisterPublicationDate: Date | null,
  commentDeadline: Date | null,
  replyCommentDeadline: Date | null
): {
  commentsPeriod: TimelinePeriod | null;
  replyCommentsPeriod: TimelinePeriod | null;
} {
  const now = new Date();
  
  // Comments period: from Federal Register publication (or FCC release if no Federal Register) to comment deadline
  let commentsPeriod: TimelinePeriod | null = null;
  if (federalRegisterPublicationDate || fccReleaseDate) {
    const commentsStart = federalRegisterPublicationDate || fccReleaseDate;
    const commentsEnd = commentDeadline;
    
    let status: 'active' | 'upcoming' | 'past' = 'past';
    if (commentsEnd) {
      if (now >= commentsStart && now <= commentsEnd) {
        status = 'active';
      } else if (now < commentsStart) {
        status = 'upcoming';
      }
    } else if (now >= commentsStart) {
      status = 'active'; // Ongoing if no end date
    } else {
      status = 'upcoming';
    }
    
    commentsPeriod = {
      start: commentsStart,
      end: commentsEnd,
      label: 'Comments Period',
      status,
    };
  }
  
  // Reply Comments period: from comment deadline to reply comment deadline
  let replyCommentsPeriod: TimelinePeriod | null = null;
  if (commentDeadline && replyCommentDeadline) {
    let status: 'active' | 'upcoming' | 'past' = 'past';
    if (now >= commentDeadline && now <= replyCommentDeadline) {
      status = 'active';
    } else if (now < commentDeadline) {
      status = 'upcoming';
    }
    
    replyCommentsPeriod = {
      start: commentDeadline,
      end: replyCommentDeadline,
      label: 'Reply Comments Period',
      status,
    };
  }
  
  return {
    commentsPeriod,
    replyCommentsPeriod,
  };
}

/**
 * Format timeline period for display
 */
export function formatTimelinePeriod(period: TimelinePeriod): {
  formattedStart: string;
  formattedEnd: string | null;
  duration: string;
  statusLabel: string;
} {
  const formattedStart = period.start.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  
  const formattedEnd = period.end
    ? period.end.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;
  
  // Calculate duration
  let duration = '';
  if (period.end) {
    const diffTime = period.end.getTime() - period.start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 1) {
      duration = '1 day';
    } else if (diffDays < 30) {
      duration = `${diffDays} days`;
    } else {
      const weeks = Math.floor(diffDays / 7);
      duration = `${weeks} week${weeks === 1 ? '' : 's'}`;
    }
  } else {
    duration = 'Ongoing';
  }
  
  const statusLabel =
    period.status === 'active'
      ? 'Active'
      : period.status === 'upcoming'
      ? 'Upcoming'
      : 'Past';
  
  return {
    formattedStart,
    formattedEnd,
    duration,
    statusLabel,
  };
}

/**
 * Determine period status (active, upcoming, past)
 */
export function determinePeriodStatus(
  start: Date,
  end: Date | null
): 'active' | 'upcoming' | 'past' {
  const now = new Date();
  
  if (end) {
    if (now >= start && now <= end) {
      return 'active';
    } else if (now < start) {
      return 'upcoming';
    } else {
      return 'past';
    }
  } else {
    // No end date - check if period has started
    if (now >= start) {
      return 'active';
    } else {
      return 'upcoming';
    }
  }
}

/**
 * Build complete timeline data structure from dates
 */
export function buildTimelineData(
  fccReleaseDate: Date,
  federalRegisterPublicationDate: Date | null,
  commentDeadline: Date | null,
  replyCommentDeadline: Date | null
): TimelineData {
  const { commentsPeriod, replyCommentsPeriod } = calculateTimeWindows(
    fccReleaseDate,
    federalRegisterPublicationDate,
    commentDeadline,
    replyCommentDeadline
  );
  
  return {
    fccReleaseDate,
    federalRegisterPublicationDate,
    commentDeadline,
    replyCommentDeadline,
    commentsPeriod,
    replyCommentsPeriod,
  };
}

